import { useCallback, useEffect, useRef, useState } from "react";
import { transcriptionApi, TranscriptionResult } from "@shared/api/transcription.api";
import { sendMetrikaGoal, sendMetrikaGoalOnce } from "@shared/helper_lib/metrika";
import { TranscriptionStatus } from "@shared/types";

interface UseTranscriptionResult {
  transcription: TranscriptionResult | null;
  isProcessing: boolean;
  error: string | null;
  startTranscription: (
    fileIdOrUrl: number | string,
    fileName: string,
    language?: string,
    model?: string,
    estimatedMinutes?: number,
    detectLanguage?: boolean,
    preview?: boolean
  ) => Promise<void>;
  reset: () => void;
}

const POLLING_INTERVAL = 3000;

/**
 * Откуда пришла запись: нужно, чтобы подкручивать посадочные под то, что
 * реально загружают, а не под все программные URL сразу.
 */
const detectJobSource = (videoUrl: string | null): string => {
  if (!videoUrl) {
    return "upload";
  }
  if (/youtube\.com|youtu\.be/i.test(videoUrl)) {
    return "youtube";
  }
  if (/vk\.com|vkvideo\.ru|vk\.ru/i.test(videoUrl)) {
    return "vk";
  }
  if (/rutube\.ru/i.test(videoUrl)) {
    return "rutube";
  }
  return "other_url";
};

export const useTranscription = (): UseTranscriptionResult => {
  const [transcription, setTranscription] = useState<TranscriptionResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const pollStatus = useCallback(
    async (transcriptionId: string) => {
      try {
        const status = await transcriptionApi.getStatus(transcriptionId);

        if (status.status === TranscriptionStatus.COMPLETED || status.status === TranscriptionStatus.FAILED) {
          stopPolling();
          const result = await transcriptionApi.getResult(transcriptionId);
          setTranscription(result);
          setIsProcessing(false);

          if (status.status === TranscriptionStatus.COMPLETED && result.isPreview) {
            sendMetrikaGoalOnce(`preview_completed:${result.id}`, "preview_completed");
          }

          if (status.status === TranscriptionStatus.FAILED) {
            setError("Ошибка при обработке файла");
          }
        }
      } catch (err) {
        stopPolling();
        const errorMessage = err instanceof Error ? err.message : "Ошибка при получении статуса";
        setError(errorMessage);
        setIsProcessing(false);
      }
    },
    [stopPolling]
  );

  const startTranscription = useCallback(
    async (
      fileIdOrUrl: number | string,
      fileName: string,
      language = "",
      model = "nova-3",
      estimatedMinutes?: number,
      detectLanguage?: boolean,
      preview?: boolean
    ) => {
      setIsProcessing(true);
      setError(null);
      setTranscription(null);
      stopPolling();

      try {
        // Определяем, это fileId или videoUrl
        const isUrl = typeof fileIdOrUrl === "string";
        const job = await transcriptionApi.createTranscription({
          fileId: isUrl ? undefined : fileIdOrUrl,
          videoUrl: isUrl ? fileIdOrUrl : undefined,
          fileName,
          language,
          model,
          estimatedMinutes,
          detectLanguage,
          preview
        });

        if (job.isPreview) {
          sendMetrikaGoal("preview_started");
        }

        sendMetrikaGoal("job_source", { job_source: detectJobSource(isUrl ? fileIdOrUrl : null) });

        setTranscription({
          id: job.jobId,
          fileId: isUrl ? null : fileIdOrUrl,
          fileName,
          text: "",
          language,
          duration: 0,
          status: job.status,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isPreview: job.isPreview,
          previewLimitSeconds: null,
          totalDurationSeconds: null,
          recommendedPlan: null,
          sourceAvailable: true
        });

        pollingIntervalRef.current = setInterval(() => {
          pollStatus(job.jobId);
        }, POLLING_INTERVAL);

        await pollStatus(job.jobId);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Ошибка при создании транскрипции";
        setError(errorMessage);
        setIsProcessing(false);
        // Пробрасываем ошибку наверх для обработки в UploadSection
        throw err;
      }
    },
    [pollStatus, stopPolling]
  );

  const reset = useCallback(() => {
    stopPolling();
    setTranscription(null);
    setIsProcessing(false);
    setError(null);
  }, [stopPolling]);

  useEffect(() => {
    return (): void => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    transcription,
    isProcessing,
    error,
    startTranscription,
    reset
  };
};
